import { useState, useEffect } from "react";

interface CapacityMetricsData {
  base: {
    net_surplus_deficit: {
      value: number;
      pct_change: number;
    };
  };
  simulation: {
    net_surplus_deficit: {
      value: number;
      pct_change: number;
    };
  };
  optimal: {
    net_surplus_deficit: {
      value: number;
      pct_change: number;
    };
  };
}

interface MetricCard {
  label: string;
  heading:string;
  subLabel:string;
  value: string;
  unit: string;
}

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <div
          key={item}
          className={`${
            item >= 5
              ? "bg-[#EFFCFF] border-[#34656D]"
              : "bg-[#FFFAF6] border-[#F1935C]"
          } rounded-3xl shadow-md border px-4 py-4 animate-pulse`}
        >
          <div className="space-y-2">
            {/* Label Skeleton */}
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            
            {/* Value Skeleton */}
            <div className="flex items-baseline gap-2">
              <div className="h-8 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaMetricsVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: CapacityMetricsData[];
}) {
  const [capacityData, setCapacityData] = useState<MetricCard[]>([]);
  // Format number with commas
  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString("en-IN");
  };
  const cardStyles = [
    "bg-[#ECF3FF80] border-[#617499] text-[#819ACC]", 
    "bg-[#FFFAF6] border-[#F1935C] text-[#F1935C]", 
    "bg-[#EFFCFF] border-[#34656D] text-[#34656D]",
    "bg-[#FFF6F7] border-[#FF1028] text-[#FF1028]",
  ];
  const textStyles = [
    "text-[#819ACC]", 
    "text-[#F1935C]", 
    "text-[#34656D]",
    "text-[#FF1028]",
  ];

 useEffect(() => {
   if (data && data.length > 0) {
     const apiData = data[0]; // Add [0] to access the first item in the array

     const formattedData: MetricCard[] = [
       {
         label: "Net Surplus/Deficit",
         heading: "Base",
         subLabel: "Considering No Offers",
         value: formatNumber(apiData.base.net_surplus_deficit.value),
         unit: "MU",
       },
       {
         label: "Net Surplus/Deficit",
         heading: "Simulated",
         subLabel: "Considering Selected Offers",
         value: formatNumber(apiData.simulation.net_surplus_deficit.value),
         unit: "MU",
       },
       {
         label: "Net Surplus/Deficit",
         heading: "Optimal",
         subLabel: "Considering Optimal Offers",
         value: formatNumber(apiData.optimal.net_surplus_deficit.value),
         unit: "MU",
       },
     ];

     setCapacityData(formattedData);
   }
 }, [data, refreshKey]);
  

  return (
    <>
      {loading ? (
        <div className="w-full bg-white border-b border-[#DFEAF2]">
          <div className="flex flex-nowrap items-center justify-around py-2 px-2">
            <div className="w-full h-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full bg-white border-b border-[#DFEAF2] rounded-2xl">
          <div className="flex flex-nowrap items-center justify-around py-4 px-2">
            <div className="w-full h-full">
              {capacityData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-4">
                  {capacityData.map((item, index) => (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <h3
                            className={`pl-2 text-sm font-medium ${""} ${
                              textStyles[index % 4]
                            }`}
                          >
                            {item.heading}
                            <span className="ml-2 text-[10px] text-[#8C8A96]">
                              {item.subLabel}
                            </span>
                          </h3>
                        </div>
                        <div
                          key={index}
                          className={`${
                            cardStyles[index % 4]
                          } rounded-3xl border px-4 py-6 transition-all hover:shadow-lg`}
                        >
                          <div className="flex flex-wrap items-center justify-between">
                            <h3 className={`text-sm font-medium ${""}`}>
                              {item.label}
                            </h3>
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span
                                className={`text-lg text-[#2B3674] ${
                                  index >= 4 ? "font-medium" : "font-medium"
                                }`}
                              >
                                {item.value} {item.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}